import {FunctionComponent, useEffect, useMemo, useState} from "react";
import { FormPartComponent, FormPartType, SupportedFormParts } from "./FormParts/FormParts";
import { OrderDetails } from "./FormParts/OrderDetails";
import { Input } from "./FormParts/Input";
import { Terms } from "./FormParts/Terms";
import { Select } from "./FormParts/Select";
import { MarketingConsent } from "./FormParts/MarketingConsent";
import { AgreementCheckbox } from "./FormParts/AgreementCheckbox";
import { OrderedList } from "@/lib/TicketApi";
import { FormComponent } from "@/lib/TicketApi";
import * as m from "@/paraglide/messages.js";
import { resetValidationSummaryReveal, useValidationSummaryRevealed } from "./validationSummaryReveal";

interface CustomerDetailsFormProps {
    form: OrderedList<FormComponent>;
    answers: Record<string, object[] | string[] | string>;
    errors: Record<string, string[]>;
    setAnswers: (answers: Record<string, object[] | string[] | string>) => void;
    setFormValid: (valid: boolean) => void;
}

/** A form part's own label, for naming it in the summary. */
function partLabel(formPart: FormComponent): string {
    const label = (formPart as unknown as { label?: unknown }).label;
    if (typeof label === 'string' && label.trim() !== '') {
        return label;
    }
    // The terms checkbox carries no label of its own.
    if (formPart.type === FormPartType.Terms) {
        return m.form_terms_question();
    }
    return m.form_unnamed_question();
}

/**
 * Server errors arrive per form-part key, but a part that validates several subfields at once
 * (orderDetails, terms) returns a nested bag — `{orderDetails: {email: [...], first_name: [...]}}`
 * — rather than a flat list. Flatten so the summary prints messages instead of "[object Object]".
 */
function flattenMessages(value: unknown): string[] {
    if (typeof value === 'string') return [value];
    if (Array.isArray(value)) return value.flatMap(flattenMessages);
    if (value && typeof value === 'object') return Object.values(value).flatMap(flattenMessages);
    return [];
}

function isBlank(value: unknown): boolean {
    return value === undefined || value === null || value === ''
        || (Array.isArray(value) && value.length === 0);
}

/**
 * Did this value change because the visitor did something? A part re-reporting what it was given
 * (or reporting empty when there was nothing) is just it mounting.
 */
function isRealChange(previous: unknown, next: unknown): boolean {
    if (isBlank(previous) && isBlank(next)) return false;
    return JSON.stringify(previous) !== JSON.stringify(next);
}

export function CustomerDetailsForm({form, answers, errors, setAnswers, setFormValid}: CustomerDetailsFormProps) {
    const [formPartValidStatus, setFormPartValidStatus] = useState<Record<string, boolean>>({});
    // Questions the visitor has actually answered or changed. Every part reports a value on mount,
    // which is not the visitor doing anything, and ordering tricks do not separate the two — the
    // widget renders under StrictMode, so mount effects run twice and any "the mount pass is over
    // now" flag is already true for the second round. What does hold is the value itself: a mount
    // reports what is already in `answers` (usually nothing), while a real interaction changes it.
    const [touched, setTouched] = useState<string[]>([]);
    // Questions the visitor has edited since this batch of server errors arrived. Only the
    // checkout provider clears its own errors on edit; everywhere else the rejection stayed on
    // screen until the next submit, so a corrected field kept being called wrong. Keyed on the
    // errors' CONTENT, not the object's identity, so a parent that rebuilds the prop each render
    // cannot turn this into a render loop.
    const errorsKey = JSON.stringify(errors);
    const [editedSinceErrors, setEditedSinceErrors] = useState<string[]>([]);
    useEffect(() => {
        setEditedSinceErrors([]);
    }, [errorsKey]);

    const formPartComponents = {
        [FormPartType.OrderDetail]: OrderDetails,
        [FormPartType.Input]: Input,
        [FormPartType.Terms]: Terms,
        [FormPartType.Select]: Select,
        [FormPartType.MarketingConsent]: MarketingConsent,
        [FormPartType.AgreementCheckbox]: AgreementCheckbox,
    } as {[k in FormPartType]: FunctionComponent<FormPartComponent<SupportedFormParts>>};

    const parts = useMemo(
        () => Object.values(form).filter((formPart: FormComponent) => formPart.type in formPartComponents),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [form],
    );

    const allFormPartsAreValid = Object.keys(formPartValidStatus).every((key) => {
        return formPartValidStatus[key];
    });

    useEffect(() => {
        setFormValid(allFormPartsAreValid);
    }, [allFormPartsAreValid, setFormValid]);

    const scrollToPart = (key: string) => {
        const target = document.querySelector(`[data-form-part="${CSS.escape(key)}"]`);
        target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        target?.querySelector<HTMLElement>('input, select, textarea')?.focus({ preventScroll: true });
    };

    // Questions the visitor still has to answer. Naming them the moment a first field is filled in
    // is nagging, not helping — half the form is legitimately still empty at that point. So the
    // list waits for a visitor who is done and stuck: every question has been touched and some are
    // still not right, or they reached for the button that is not working (hover, or a tap on a
    // phone, where there is no hover to reach for).
    const revealed = useValidationSummaryRevealed();
    const everyPartTouched = parts.length > 0 && parts.every((formPart) => touched.includes(formPart.key));
    const incomplete = revealed || everyPartTouched
        ? parts.filter((formPart) => formPartValidStatus[formPart.key] === false)
        : [];

    // A form that has just appeared starts quiet: reaching for the button on the step before this
    // one says nothing about this one. Keyed on which questions there are rather than on the prop's
    // identity, so a parent that rebuilds `form` each render cannot keep wiping the reveal.
    const partsKey = parts.map((formPart) => formPart.key).join('|');
    useEffect(() => {
        resetValidationSummaryReveal();
    }, [partsKey]);

    // Anything the server rejected. Shown regardless of interaction — a submission that came
    // back 422 has to explain itself even if the answers were prefilled and never touched.
    const rejected = parts
        .filter((formPart) => !editedSinceErrors.includes(formPart.key))
        .map((formPart) => ({ formPart, messages: flattenMessages(errors[formPart.key]) }))
        .filter(({ messages }) => messages.length > 0);

    // Errors the server sent under a key that belongs to no form part (`general`, or a field the
    // form no longer has). These used to render nowhere at all, so a failed save looked silent.
    const partKeys = new Set(parts.map((formPart) => formPart.key));
    const unattributed = Object.entries(errors)
        .filter(([key]) => !partKeys.has(key))
        .flatMap(([, value]) => flattenMessages(value));

    // Take the visitor to the first thing the server complained about — it is regularly above or
    // below the fold, which is what made a rejected submission feel like nothing happened.
    const firstRejectedKey = rejected[0]?.formPart.key;
    useEffect(() => {
        if (firstRejectedKey !== undefined) {
            scrollToPart(firstRejectedKey);
        }
    }, [firstRejectedKey]);

    const showSummary = incomplete.length > 0 || rejected.length > 0 || unattributed.length > 0;
    const isRejection = rejected.length > 0 || unattributed.length > 0;

    return (
        <>
            {parts.map((formPart: FormComponent, index: number) => {
                const Component = formPartComponents[formPart.type as FormPartType];
                return (
                    <Component 
                        key={index}
                        formPart={formPart as SupportedFormParts}
                        answers={answers[formPart.key] ?? []}
                        errors={editedSinceErrors.includes(formPart.key) ? [] : errors[formPart.key] ?? []}
                        // Functional update: every form part reports its validity on mount, so a
                        // spread of the captured state drops all but the last reporter's answer.
                        onValidStatusChange={(formPartType: string, value: boolean) => setFormPartValidStatus((current) => ({
                            ...current,
                            [formPartType]: value
                        }))}
                        onValueChange={(formPartType: string, value: string|object) => {
                            if (isRealChange(answers[formPartType], value)) {
                                setTouched((current) => current.includes(formPartType)
                                    ? current
                                    : [...current, formPartType]);
                                setEditedSinceErrors((current) => current.includes(formPartType)
                                    ? current
                                    : [...current, formPartType]);
                            }
                            // @ts-expect-error todo, check how to correctly type this
                            setAnswers({
                                ...answers,
                                [formPartType]: value
                            });
                        }}
                    />
                )
            })}

            {showSummary && (
                <div
                    role="alert"
                    aria-live="polite"
                    className="mt-4 rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-800"
                >
                    <p className="font-medium">
                        {isRejection ? m.form_has_errors_title() : m.form_incomplete_title()}
                    </p>
                    <p className="mt-0.5 opacity-80">
                        {isRejection ? m.form_has_errors_intro() : m.form_incomplete_intro()}
                    </p>
                    <ul className="mt-2 space-y-1">
                        {rejected.map(({ formPart, messages }) => (
                            <li key={`rejected-${formPart.key}`}>
                                <button
                                    type="button"
                                    onClick={() => scrollToPart(formPart.key)}
                                    className="text-left underline underline-offset-2 hover:opacity-80"
                                >
                                    {partLabel(formPart)}
                                </button>
                                <span className="opacity-80"> — {messages.join(' ')}</span>
                            </li>
                        ))}
                        {incomplete
                            .filter((formPart) => !rejected.some((r) => r.formPart.key === formPart.key))
                            .map((formPart) => (
                                <li key={`incomplete-${formPart.key}`}>
                                    <button
                                        type="button"
                                        onClick={() => scrollToPart(formPart.key)}
                                        className="text-left underline underline-offset-2 hover:opacity-80"
                                    >
                                        {partLabel(formPart)}
                                    </button>
                                </li>
                            ))}
                        {unattributed.map((message, i) => (
                            <li key={`general-${i}`}>{message}</li>
                        ))}
                    </ul>
                </div>
            )}
        </>
    )
}
